/**
 * Utility functions for managing view persistence across pages
 */

const STORAGE_KEYS = {
  TASKLIST_VIEW: 'tasklist_view_mode',
  WORKSHEET_VIEW: 'worksheet_view_mode',
  DOCUMENT_LIST_VIEW: 'document_list_view_mode',
  SHARED_DOCUMENTS_VIEW: 'shared_documents_view_mode',
  FAVORITE_DOCUMENTS_VIEW: 'favorite_documents_view_mode',
  STAFF_DOCUMENTS_VIEW: 'staff_documents_view_mode',
  VEHICLE_DOCUMENTS_VIEW: 'vehicle_documents_view_mode'
};

/**
 * Get the saved view mode for a specific page
 * @param {string} pageKey - The key for the page (e.g., 'tasklist', 'worksheet')
 * @param {string} defaultView - The default view mode if none is saved
 * @returns {string} The saved view mode or default
 */
export const getSavedViewMode = (pageKey, defaultView = 'card') => {
  try {
    let storageKey;
    if (pageKey === 'tasklist') {
      storageKey = STORAGE_KEYS.TASKLIST_VIEW;
    } else if (pageKey === 'worksheet') {
      storageKey = STORAGE_KEYS.WORKSHEET_VIEW;
    } else if (pageKey === 'documentList') {
      storageKey = STORAGE_KEYS.DOCUMENT_LIST_VIEW;
    } else if (pageKey === 'sharedDocuments') {
      storageKey = STORAGE_KEYS.SHARED_DOCUMENTS_VIEW;
    } else if (pageKey === 'favoriteDocuments') {
      storageKey = STORAGE_KEYS.FAVORITE_DOCUMENTS_VIEW;
    } else if (pageKey === 'staffDocuments') {
      storageKey = STORAGE_KEYS.STAFF_DOCUMENTS_VIEW;
    } else if (pageKey === 'vehicleDocuments') {
      storageKey = STORAGE_KEYS.VEHICLE_DOCUMENTS_VIEW;
    } else {
      return defaultView;
    }
    const savedView = localStorage.getItem(storageKey);
    return savedView || defaultView;
  } catch (error) {
    console.warn('Error reading view mode from localStorage:', error);
    return defaultView;
  }
};

/**
 * Save the view mode for a specific page
 * @param {string} pageKey - The key for the page (e.g., 'tasklist', 'worksheet')
 * @param {string} viewMode - The view mode to save
 */
export const saveViewMode = (pageKey, viewMode) => {
  try {
    let storageKey;
    if (pageKey === 'tasklist') {
      storageKey = STORAGE_KEYS.TASKLIST_VIEW;
    } else if (pageKey === 'worksheet') {
      storageKey = STORAGE_KEYS.WORKSHEET_VIEW;
    } else if (pageKey === 'documentList') {
      storageKey = STORAGE_KEYS.DOCUMENT_LIST_VIEW;
    } else if (pageKey === 'sharedDocuments') {
      storageKey = STORAGE_KEYS.SHARED_DOCUMENTS_VIEW;
    } else if (pageKey === 'favoriteDocuments') {
      storageKey = STORAGE_KEYS.FAVORITE_DOCUMENTS_VIEW;
    } else if (pageKey === 'staffDocuments') {
      storageKey = STORAGE_KEYS.STAFF_DOCUMENTS_VIEW;
    } else if (pageKey === 'vehicleDocuments') {
      storageKey = STORAGE_KEYS.VEHICLE_DOCUMENTS_VIEW;
    } else {
      return;
    }
    localStorage.setItem(storageKey, viewMode);
  } catch (error) {
    console.warn('Error saving view mode to localStorage:', error);
  }
};

/**
 * Clear saved view mode for a specific page
 * @param {string} pageKey - The key for the page (e.g., 'tasklist', 'worksheet')
 */
export const clearSavedViewMode = (pageKey) => {
  try {
    let storageKey;
    if (pageKey === 'tasklist') {
      storageKey = STORAGE_KEYS.TASKLIST_VIEW;
    } else if (pageKey === 'worksheet') {
      storageKey = STORAGE_KEYS.WORKSHEET_VIEW;
    } else if (pageKey === 'documentList') {
      storageKey = STORAGE_KEYS.DOCUMENT_LIST_VIEW;
    } else if (pageKey === 'sharedDocuments') {
      storageKey = STORAGE_KEYS.SHARED_DOCUMENTS_VIEW;
    } else if (pageKey === 'favoriteDocuments') {
      storageKey = STORAGE_KEYS.FAVORITE_DOCUMENTS_VIEW;
    } else if (pageKey === 'staffDocuments') {
      storageKey = STORAGE_KEYS.STAFF_DOCUMENTS_VIEW;
    } else if (pageKey === 'vehicleDocuments') {
      storageKey = STORAGE_KEYS.VEHICLE_DOCUMENTS_VIEW;
    } else {
      return;
    }
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.warn('Error clearing view mode from localStorage:', error);
  }
};

/**
 * Clear all saved view modes
 */
export const clearAllSavedViewModes = () => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Error clearing all view modes from localStorage:', error);
  }
};
